/**
 * How far down the line of succession a beneficiary sits.
 *
 * A will names who inherits, and who inherits in their place if they do not
 * survive the decedent. That substitution is what the level records: the
 * children take first, and only if a child has died does anyone below them take
 * that child's share. A relationship alone cannot say this, which is why the
 * level is asked for separately: "child" tells you the tie, not the order.
 *
 * The number is the depth, so a lower number takes first. What sits at each
 * level differs from estate to estate, which is why the levels are ordered
 * rather than named after particular relatives.
 *
 * Mirrors BeneficiaryLevel on the API.
 */
export const BENEFICIARY_LEVELS: { value: number; label: string; hint: string }[] = [
  {
    value: 1,
    label: 'Primary beneficiary',
    hint: 'Takes directly, ahead of everyone below. Typically the children.',
  },
  {
    value: 2,
    label: 'Secondary beneficiary',
    hint: "Takes a primary's share where that primary has died.",
  },
  {
    value: 3,
    label: 'Tertiary beneficiary',
    hint: 'Takes where both levels above are gone. Typically the grandchildren.',
  },
  {
    value: 4,
    label: 'Quaternary beneficiary',
    hint: 'Anything further down the line.',
  },
];

/**
 * Falls back to a numbered label so a level written by a newer backend still
 * reads as something rather than as a blank.
 */
export const beneficiaryLevelLabel = (level?: number | null): string => {
  if (level === null || level === undefined) return '';
  return (
    BENEFICIARY_LEVELS.find((entry) => entry.value === level)?.label ??
    `Level ${level} beneficiary`
  );
};

/** Colour per level, so the roster reads as an order at a glance. */
export const beneficiaryLevelColor = (level?: number | null): string => {
  switch (level) {
    case 1:
      return 'green';
    case 2:
      return 'blue';
    case 3:
      return 'geekblue';
    default:
      return 'default';
  }
};

/**
 * The level a relationship implies, used to prefill the field when somebody is
 * added. It follows how a will normally reads: the children take first and
 * their spouses stand in for them. A starting point, not a rule, so the form
 * lets it be overridden, and the levels below have no suggestion because
 * nothing in a single relationship implies them: a grandchild is recorded as
 * the child of a child, not as a tie to the decedent.
 *
 * Mirrors suggestedBeneficiaryLevel on the API.
 */
export const suggestedBeneficiaryLevel = (
  relationship?: string | null,
): number | undefined => {
  switch ((relationship ?? '').trim().toLowerCase()) {
    case 'child':
      return 1;
    case 'spouse':
      return 2;
    default:
      return undefined;
  }
};
